import {
  useGetLanguageTestList,
  useGetMajorList,
  useGetSpecificSkillCertificateList,
  useGetCompanySpecificIndustryList,
} from "@/api/services/global/master";
import { IconErrorOutline } from "@/assets/icons";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { TalentIndustryFields } from "@/features/talent/components/talent-form/TalentIndustryFields";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, Grid, Collapse } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CareIndustryValidationModal } from "@/components/commons/CareIndustryValidationModal";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";

type TalentFormLanguageAbilityProps = {
  onValidationCheck?: (validateFn: () => boolean) => void;
  onContinueSubmit?: () => void;
};

export const TalentFormLanguageAbility: FCC<TalentFormLanguageAbilityProps> = ({
  onValidationCheck,
  onContinueSubmit,
}) => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue } = methods;

  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { getCertificatesByIndustryId } = useGetSpecificSkillCertificateList();
  const { majorOptions, getJobTypeListOptionByMajor } = useGetMajorList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();

  const companySpecificIndustryId = useWatch({ control, name: "companySpecificIndustryId" });
  const languageTestLevelIds = useWatch({ control, name: "languageTestLevelIds" });

  const [showCareIndustryValidationModal, setShowCareIndustryValidationModal] = useState(false);

  // Check if selected language test is NAT-TEST
  const isNatTestSelected = useMemo(() => {
    if (!languageTestLevelIds || !Array.isArray(languageTestLevelIds) || languageTestLevelIds.length === 0) return false;
    return languageTestLevelIds.some((id) => {
      if (id === null || id === undefined) return false;
      const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
      return selectedOption?.label?.includes("NAT-TEST") ?? false;
    });
  }, [languageTestLevelIds, languageTestLevelOptions]);

  const validateCareIndustryRequirement = () => {
    const currentIsCareIndustrySelected = companySpecificIndustryId
      ? (companySpecificIndustryOptions.find((opt) => Number(opt.value) === Number(companySpecificIndustryId))?.label?.includes("介護") ?? false)
      : false;

    const currentIsCareIndustryJapaneseTestSelected =
      languageTestLevelIds &&
      Array.isArray(languageTestLevelIds) &&
      languageTestLevelIds.length > 0 &&
      languageTestLevelIds.some((id) => {
        if (id === null || id === undefined) return false;
        const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
        return selectedOption?.label?.includes("介護日本語評価試験") ?? false;
      });

    if (currentIsCareIndustrySelected && !currentIsCareIndustryJapaneseTestSelected) {
      setShowCareIndustryValidationModal(true);
      return false;
    }
    return true;
  };

  // Function to handle modal close
  const handleModalClose = () => {
    setShowCareIndustryValidationModal(false);
  };

  // Function to handle modal continue
  const handleModalContinue = () => {
    setShowCareIndustryValidationModal(false);
    // Continue with form submission
    if (onContinueSubmit) {
      onContinueSubmit();
    }
  };

  // Expose validation function to parent
  useEffect(() => {
    if (onValidationCheck) {
      onValidationCheck(validateCareIndustryRequirement);
    }
  }, [onValidationCheck, validateCareIndustryRequirement]);

  const majorId = useWatch({ control, name: "majorId" });
  const jobTypeId = useWatch({ control, name: "jobTypeId" }) as number | null | undefined;
  const prevCompanySpecificIndustryIdRef = useRef<number | null | undefined>(undefined);
  const prevMajorIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (!companySpecificIndustryId) {
      const currentValue = methods.getValues("specificSkillCertificates");
      if (currentValue !== null && currentValue !== undefined) {
        setValue("specificSkillCertificates", null, { shouldDirty: false });
      }
    }
  }, [companySpecificIndustryId, setValue, methods]);

  useEffect(() => {
    if (prevCompanySpecificIndustryIdRef.current === undefined) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;
      return;
    }

    if (prevCompanySpecificIndustryIdRef.current !== companySpecificIndustryId) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;
      setValue("specificSkillCertificates", null, { shouldDirty: false });
    }
  }, [companySpecificIndustryId, setValue]);

  useEffect(() => {
    if (prevMajorIdRef.current === undefined) {
      prevMajorIdRef.current = majorId;
      if (!majorId) {
        setValue("jobTypeId", null, { shouldDirty: false });
      }
      return;
    }

    if (prevMajorIdRef.current !== majorId) {
      prevMajorIdRef.current = majorId;

      if (!majorId) {
        setValue("jobTypeId", null, { shouldDirty: false });
        return;
      }

      const validOptions = getJobTypeListOptionByMajor(Number(majorId));
      const validOptionValues = new Set(validOptions.map((opt) => opt.value));

      if (jobTypeId !== null && jobTypeId !== undefined) {
        if (!validOptionValues.has(jobTypeId)) {
          setValue("jobTypeId", null, { shouldDirty: false });
        }
      }
    }
  }, [majorId, jobTypeId, getJobTypeListOptionByMajor, setValue]);

  const { expanded: languageExpanded, toggle: languageToggle } = useCollapsible();

  return (
    <>
      <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
        <CollapsibleSectionHeader title="分野・日本語能力" expanded={languageExpanded} onToggle={languageToggle} />

        <Collapse in={languageExpanded}>
        <TalentIndustryFields />

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name="languageTestLevelIds">
                <SelectMulti placeholder="選択してください" options={languageTestLevelOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="languageTestOther">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        {isNatTestSelected && (
          <Stack mt={1}>
            <Typography variant="cap12" color={black[70]}>
              <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: state.warning_1 }}>
                <IconErrorOutline fontSize="20px" />
                特定技能日本語能力評価対象外（日本語能力レベル参照）
              </Box>
            </Typography>
          </Stack>
        )}

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name="specificSkillCertificates">
                <SelectMulti
                  placeholder="選択してください"
                  options={companySpecificIndustryId ? getCertificatesByIndustryId(companySpecificIndustryId) : []}
                  separator="、"
                />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name="majorId">
                <Select placeholder="選択してください" options={majorOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="jobTypeId">
                <Select placeholder="選択してください" options={getJobTypeListOptionByMajor(Number(majorId))} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
        </Collapse>
      </InnerBox>

      <CareIndustryValidationModal open={showCareIndustryValidationModal} onClose={handleModalClose} onContinue={handleModalContinue} />
    </>
  );
};
